/* src/components/inbox/chips.jsx
 * Chip renderers bound to real conversation fields (README "Chip system").
 * Never fabricate a chip — every chip here reads a field that exists on
 * `conversations` docs today.
 */

import React from 'react';

export const DEAL_STAGES = [
  { id: 'new', label: 'New' },
  { id: 'working', label: 'Working' },
  { id: 'proposal_sent', label: 'Proposal sent' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

export function dealStageLabel(stageId) {
  return DEAL_STAGES.find((s) => s.id === stageId)?.label || 'New';
}

/** Compact chip row for a lead-list row. */
export function LeadRowChips({ lead }) {
  const stageId = lead.dealStage || 'new';
  return (
    <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
      {lead.status === 'booked' || lead.manuallyBooked ? (
        <span className="inbox-chip accent">BOOKED</span>
      ) : lead.aiEnabled === false ? (
        <span className="inbox-chip warn">NEEDS YOU</span>
      ) : (
        <span className="inbox-chip accent-soft">OPERATOR ON</span>
      )}
      <span className="inbox-chip" style={stageId === 'lost' ? { opacity: 0.75 } : undefined}>
        {dealStageLabel(stageId).toUpperCase()}
      </span>
      {lead.spamStatus === 'clean' && (
        <span className="inbox-chip accent-ink">CLEAN{lead.spamScore != null ? ` \u00b7 ${lead.spamScore}` : ''}</span>
      )}
      {lead.reviewFlag === 'buying_held_suspicious' ? (
        <span className="inbox-chip warn">NEEDS YOUR OK</span>
      ) : lead.spamStatus === 'suspicious' ? (
        <span className="inbox-chip warn">SUSPICIOUS{lead.spamScore != null ? ` \u00b7 ${lead.spamScore}` : ''}</span>
      ) : null}
      {lead.status === 'no_response' && <span className="inbox-chip danger">NO RESPONSE</span>}
      {lead.status === 'blocked' && <span className="inbox-chip danger">BLOCKED</span>}
      {lead.reviewFlag === 'auto_paused_loop_suspected' && (
        <span className="inbox-chip warn">LOOP PAUSED</span>
      )}
      {lead.reviewFlag === 'auto_paused_ai_flagged_scam' && (
        <span className="inbox-chip warn">AI FLAGGED</span>
      )}
      {lead.reviewFlag === 'auto_paused_selling_to_us' && (
        <span className="inbox-chip warn">SELLING TO YOU</span>
      )}
      {lead.reviewFlag === 'auto_paused_intent_unclear' && (
        <span className="inbox-chip warn">INTENT UNCLEAR</span>
      )}
      {lead.reviewFlag === 'buying_held_suspicious' && (
        <span className="inbox-chip accent-ink">AI HELD · BUYER</span>
      )}
      {lead.reviewFlag === 'buying_auto_released' && (
        <span className="inbox-chip accent-ink">AUTO-APPROVED · BUYER</span>
      )}
      {lead.reviewFlag === 'lead_proposed_meeting' && (
        <span className="inbox-chip warn">NEEDS YOU · THEY SENT A BOOKING LINK</span>
      )}
      {lead.reviewFlag === 'needs_reply' && (
        <span className="inbox-chip warn">NEEDS YOU · REPLY</span>
      )}
      {lead.isTest && <span className="inbox-chip">TEST</span>}
      {lead.isResponseTest && <span className="inbox-chip">RESPONSE TEST</span>}
      {lead.source === 'chat' && <span className="inbox-chip accent-soft">CHAT</span>}
    </div>
  );
}

/**
 * Resolve a display source from `conversation.attribution`
 * (shape: { clickIds, utms, landingUrl, referrer } — see
 * functions-forms/src/shared/attribution.ts). Returns null when there is
 * nothing to show — the attribution card hides itself in that case.
 */
export function resolveAttributionSource(attribution) {
  if (!attribution) return null;
  const clickIds = attribution.clickIds || {};
  const utms = attribution.utms || {};
  const hasAnything =
    Object.keys(clickIds).length > 0 || Object.keys(utms).length > 0 || attribution.landingUrl || attribution.referrer;
  if (!hasAnything) return null;

  if (clickIds.gclid || clickIds.gbraid || clickIds.wbraid) {
    return { label: 'Google Ads', clickIdKey: clickIds.gclid ? 'gclid' : clickIds.gbraid ? 'gbraid' : 'wbraid', clickId: clickIds.gclid || clickIds.gbraid || clickIds.wbraid };
  }
  if (clickIds.fbclid) return { label: 'Meta', clickIdKey: 'fbclid', clickId: clickIds.fbclid };
  if (clickIds.ttclid) return { label: 'TikTok', clickIdKey: 'ttclid', clickId: clickIds.ttclid };
  if (clickIds.msclkid) return { label: 'Microsoft Ads', clickIdKey: 'msclkid', clickId: clickIds.msclkid };
  if (utms.utm_source) return { label: utms.utm_source, clickIdKey: null, clickId: null };
  return { label: 'Organic', clickIdKey: null, clickId: null };
}

function truncateClickId(value) {
  if (!value) return '';
  if (value.length <= 10) return value;
  return `\u2026${value.slice(-8)}`;
}

export function ClickIdBadge({ clickIdKey, clickId }) {
  if (!clickIdKey || !clickId) return null;
  return <span className="mono" style={{ fontSize: '11px' }}>{clickIdKey} {truncateClickId(clickId)}</span>;
}

const OUTCOME_LABEL = { accepted: 'ACCEPTED \u2713', test: 'TEST', skipped: 'SKIPPED' };

/** Won-context "CONVERSION ↑" chips — reads conversation.conversionUploads[] (live). */
export function ConversionChips({ conversionUploads }) {
  if (!Array.isArray(conversionUploads) || conversionUploads.length === 0) return null;
  const latest = conversionUploads[conversionUploads.length - 1];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {latest.outcomes.map((o, idx) => {
        const label = `CONVERSION \u2191 ${String(o.platform).toUpperCase()} \u00b7 ${OUTCOME_LABEL[o.outcome] || String(o.outcome).toUpperCase()}`;
        return (
          <span
            key={`${o.platform}-${idx}`}
            className={`inbox-chip ${o.outcome === 'accepted' ? 'accent-soft' : o.outcome === 'skipped' ? 'danger' : ''}`}
            style={{ padding: '4px 8px' }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}
